if (!customElements.get('bundle-deal')) {
  customElements.define(
    'bundle-deal',
    class BundleDeal extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.closest('product-info')?.dataset.section;
        this.toggleBtn = this.querySelector('[data-bundle-toggle]');
        this.addBtn = this.querySelector('[data-bundle-add]');
        this.action = this.querySelector('.bundle__action');
        this.savingsAmountElement = this.querySelector('[data-bundle-savings-amount]');
        this.discountRate = parseFloat(this.dataset.discountRate) || 0.30;
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (this.dataset.recommendationMode === 'true') {
          this.fetchRecommendation();
          return;
        }

        this.initMetafieldMode();
      }

      initMetafieldMode() {
        this.variantInput = this.querySelector('[data-bundle-variant]');
        const savingsScript = this.querySelector('[data-bundle-savings]');
        this.savingsByVariantPair = savingsScript ? JSON.parse(savingsScript.textContent) : {};

        this.updateSavings = this.updateSavings.bind(this);
        this.toggleBtn?.addEventListener('click', this.onToggle.bind(this));
        this.addBtn?.addEventListener('click', this.onAdd.bind(this));
        this.variantInput?.addEventListener('change', this.updateSavings);
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data?.sectionId !== this.sectionId) return;
            this.updateSavings();
            this.setMainAvailability(event.data.variant);
          });
        }
        this.updateSavings();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      setMainAvailability(variant) {
        if (!this.addBtn) return;
        this.addBtn.disabled = !variant || variant.available === false;
      }

      // ── Metafield mode savings (pre-computed JSON map) ──

      updateSavings() {
        if (!this.savingsAmountElement) return;
        if (this.isRecommendationMode) {
          this.updateRecommendationSavings();
          return;
        }
        const formattedSavings =
          this.savingsByVariantPair[`${this.currentVariantId}:${this.variantInput?.value}`];
        if (formattedSavings) this.savingsAmountElement.textContent = formattedSavings;
      }

      // ── Recommendation mode ──

      fetchRecommendation() {
        this.isRecommendationMode = true;
        const productId = this.dataset.productId;
        if (!productId) { this.remove(); return; }

        fetch(`/recommendations/products.json?product_id=${productId}&limit=10`)
          .then((r) => r.json())
          .then((data) => {
            const products = data.products || [];
            const rec = products.find((p) => {
              if (p.id === parseInt(productId)) return false;
              const h = (p.handle || '').toLowerCase();
              const t = (p.title || '').toLowerCase();
              if (h.includes('recura') || t.includes('recura')) return false;
              if (h.includes('covered-returns') || h.includes('shipping-protection')) return false;
              // Only recommend Kindle cases (multiple variants with Kindle model names)
              const variants = p.variants || [];
              if (variants.length < 2) return false;
              const hasKindleVariant = variants.some((v) =>
                /kindle|paperwhite|colorsoft/i.test(v.title)
              );
              if (!hasKindleVariant) return false;
              const price = variants[0]?.price;
              if (!price || price === '0.00' || price === 0) return false;
              return true;
            });
            if (!rec) { this.remove(); return; }
            this.populateRecommendation(rec);
          })
          .catch(() => this.remove());
      }

      populateRecommendation(product) {
        this.recProduct = product;

        // Image
        const imageLink = this.querySelector('[data-rec-image]');
        if (imageLink && product.featured_image) {
          imageLink.href = product.url;
          const img = document.createElement('img');
          img.src = product.featured_image;
          img.alt = product.title;
          img.loading = 'lazy';
          img.width = 120;
          img.height = 120;
          imageLink.appendChild(img);
        } else if (imageLink) {
          imageLink.href = product.url;
        }

        // Name + URL
        const nameLink = this.querySelector('[data-rec-name]');
        if (nameLink) {
          nameLink.href = product.url;
          nameLink.textContent = product.title;
        }

        // Toggle aria-label
        if (this.toggleBtn) {
          this.toggleBtn.setAttribute('aria-label', product.title);
        }

        // Variants
        const variants = product.variants || [];
        const selectWrapper = this.querySelector('[data-rec-select]');
        const hiddenInput = this.querySelector('[data-rec-hidden-variant]');
        const selectEl = selectWrapper?.querySelector('select[data-bundle-variant]');

        if (variants.length > 1 && selectEl) {
          // Multiple variants — populate select, remove hidden input
          variants.forEach((v) => {
            const opt = document.createElement('option');
            opt.value = v.id;
            opt.textContent = `${v.title} · ${this.formatMoney(v.price)}`;
            if (!v.available) opt.disabled = true;
            selectEl.appendChild(opt);
          });
          selectWrapper.hidden = false;
          hiddenInput?.remove();
          this.variantInput = selectEl;
        } else if (variants.length === 1) {
          // Single variant — use hidden input, remove select wrapper
          if (hiddenInput) hiddenInput.value = variants[0].id;
          selectWrapper?.remove();
          this.variantInput = hiddenInput;
        }

        // Price — show compare (original strikethrough) + discounted price
        const firstVariant = variants[0];
        const priceEl = this.querySelector('[data-rec-price]');
        if (priceEl && firstVariant) {
          this.renderComparePrice(priceEl, firstVariant.price);
        }

        // Wire up events
        this.updateSavings = this.updateSavings.bind(this);
        this.toggleBtn?.addEventListener('click', this.onToggle.bind(this));
        this.addBtn?.addEventListener('click', this.onAdd.bind(this));
        this.variantInput?.addEventListener('change', () => {
          this.updateRecommendationPrice();
          this.updateRecommendationSavings();
        });
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data?.sectionId !== this.sectionId) return;
            this.setMainAvailability(event.data.variant);
          });
        }

        // Compute initial savings
        this.updateRecommendationSavings();

        // Show the component
        this.hidden = false;
      }

      updateRecommendationSavings() {
        if (!this.savingsAmountElement || !this.recProduct) return;
        const selectedId = this.variantInput?.value;
        const variant = this.recProduct.variants.find((v) => v.id === parseInt(selectedId));
        if (!variant) return;
        const savings = Math.round(variant.price * this.discountRate);
        this.savingsAmountElement.textContent = this.formatMoney(savings);
      }

      updateRecommendationPrice() {
        const priceEl = this.querySelector('[data-rec-price]');
        if (!priceEl || !this.recProduct) return;
        const selectedId = this.variantInput?.value;
        const variant = this.recProduct.variants.find((v) => v.id === parseInt(selectedId));
        if (variant) this.renderComparePrice(priceEl, variant.price);
      }

      renderComparePrice(el, originalCents) {
        const discounted = Math.ceil(originalCents * (1 - this.discountRate));
        el.innerHTML = `<s class="bundle__compare-price">${this.formatMoney(originalCents)}</s> <span class="bundle__sale-price">${this.formatMoney(discounted)}</span>`;
      }

      formatMoney(cents) {
        return '$' + (cents / 100).toFixed(2);
      }

      // ── Shared methods ──

      onToggle() {
        const active = this.classList.toggle('bundle--paired-active');
        this.toggleBtn.setAttribute('aria-expanded', active ? 'true' : 'false');
        if (this.action) this.action.hidden = !active;
      }

      get currentVariantId() {
        const liveId = document.querySelector(
          `#product-form-${this.sectionId} input[name="id"]`
        )?.value;
        return Number(liveId || this.dataset.currentVariant);
      }

      get items() {
        const items = [{ id: this.currentVariantId, quantity: 1 }];
        const pairedActive = this.classList.contains('bundle--paired-active');
        if (pairedActive && this.variantInput && this.variantInput.value) {
          items.push({ id: Number(this.variantInput.value), quantity: 1 });
        }
        return items;
      }

      onAdd() {
        if (this.addBtn?.disabled) return;
        const items = this.items;
        if (!items.length) return;

        this.spinner = this.addBtn.querySelector('.loading__spinner');
        this.addBtn.setAttribute('aria-disabled', 'true');
        this.addBtn.classList.add('loading');
        this.spinner?.classList.remove('hidden');

        if (!this.cart) {
          window.location = routes.cart_url;
          return;
        }

        const sections = this.cart.getSectionsToRender().map((section) => section.id);
        const body = JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) return;
            if (!response.key && response.items && response.items.length) {
              response.key = response.items[response.items.length - 1].key;
            }
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.cart.renderContents(response);
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-deal' });
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.addBtn.removeAttribute('aria-disabled');
            this.addBtn.classList.remove('loading');
            this.spinner?.classList.add('hidden');
          });
      }
    }
  );
}
